import { BaseService, type RequestOptions } from "./BaseService";

export type GlobalSearchEntityType =
  | "tenant"
  | "hotel"
  | "room"
  | "roomCategory"
  | "guest"
  | "reservation"
  | "service"
  | "serviceCategory"
  | "amenity"
  | "category"
  | "module"
  | "staff"
  | "team"
  | "request";

export interface GlobalSearchItem {
  id: string;
  label: string;
  subtitle?: string | null;
  type: GlobalSearchEntityType;
}

export interface GlobalSearchResponse {
  query: string;
  tenants: GlobalSearchItem[];
  hotels: GlobalSearchItem[];
  rooms: GlobalSearchItem[];
  roomCategories: GlobalSearchItem[];
  guests: GlobalSearchItem[];
  reservations: GlobalSearchItem[];
  services: GlobalSearchItem[];
  serviceCategories: GlobalSearchItem[];
  amenities: GlobalSearchItem[];
  categories: GlobalSearchItem[];
  modules: GlobalSearchItem[];
  staff: GlobalSearchItem[];
  teams: GlobalSearchItem[];
  requests: GlobalSearchItem[];
}

export interface GlobalSearchRequest {
  query: string;
  hotelId?: string;
  useLegacyHotelScope?: boolean;
}

export function createEmptyGlobalSearchResponse(
  query: string,
): GlobalSearchResponse {
  return {
    query,
    tenants: [],
    hotels: [],
    rooms: [],
    roomCategories: [],
    guests: [],
    reservations: [],
    services: [],
    serviceCategories: [],
    amenities: [],
    categories: [],
    modules: [],
    staff: [],
    teams: [],
    requests: [],
  };
}

class GlobalSearchService extends BaseService {
  constructor() {
    super();
  }

  async search(
    params: GlobalSearchRequest,
    options?: Omit<RequestOptions, "method" | "body">,
  ): Promise<GlobalSearchResponse> {
    const normalizedQuery = params.query.trim();

    if (normalizedQuery.length === 0) {
      return createEmptyGlobalSearchResponse(normalizedQuery);
    }

    const path =
      params.useLegacyHotelScope && params.hotelId
        ? `/hotels/${params.hotelId}/search/global`
        : "/search/global";

    return this.get<GlobalSearchResponse>(
      path,
      { query: normalizedQuery },
      options,
    );
  }
}

export const globalSearchService = new GlobalSearchService();
